import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import type { User } from '../types';

interface LoginProps {
  onLogin: (user: User) => void;
}

type Step =
  | { kind: 'credentials' }
  | { kind: 'mfa'; challenge: string };

export function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverStatus, setServerStatus] = useState<'connecting' | 'ok' | 'down'>('connecting');

  useEffect(() => {
    let cancelled = false;
    api
      .health()
      .then((data) => {
        if (cancelled) return;
        setServerStatus(data.status === 'ok' ? 'ok' : 'down');
      })
      .catch(() => {
        if (cancelled) return;
        setServerStatus('down');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.login(email, password);
      if ('mfaRequired' in res) {
        setStep({ kind: 'mfa', challenge: res.challenge });
      } else {
        onLogin(res.user);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many sign-in attempts. Wait a minute and try again.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Invalid email or password.');
      } else if (err instanceof ApiError && err.status === 400) {
        setError('Please enter a valid email and password.');
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitMfa(e: React.FormEvent) {
    e.preventDefault();
    if (step.kind !== 'mfa') return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.loginMfa(step.challenge, code);
      onLogin(res.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many code attempts. Wait a minute and try again.');
        setCode('');
      } else if (err instanceof ApiError && err.status === 401) {
        if (err.code === 'invalid_or_expired_challenge') {
          setError('Session expired. Please sign in again.');
          setStep({ kind: 'credentials' });
          setCode('');
        } else {
          setError('Invalid code. Try again, or use a backup code.');
          setCode('');
        }
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Atmospheric orbs */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-indigo-500/[0.06] blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-emerald-500/[0.04] blur-3xl pointer-events-none"
      />

      <div className="w-full max-w-sm relative animate-fade-up">
        <div className="text-center mb-10">
          <span className="inline-block rounded-full hairline px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-zinc-400 mb-5 bg-zinc-900/40">
            PGOC · Internal
          </span>
          <h1 className="text-4xl font-semibold tracking-tightest leading-[1.05]">
            Welcome back.
          </h1>
          <p className="mt-3 text-sm text-zinc-400">
            Sign in to continue to the workspace.
          </p>
        </div>

        {/* Double-bezel form shell */}
        <div className="p-1.5 rounded-3xl bg-zinc-900/30 hairline">
          <div className="rounded-[calc(1.5rem-0.375rem)] bg-zinc-950/60 hairline shadow-inset-hairline px-6 py-6">
            {step.kind === 'credentials' ? (
              <form onSubmit={onSubmitCredentials} className="space-y-4">
                <Field label="Email">
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl bg-zinc-900/60 hairline px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:hairline-strong transition-all duration-300 ease-spring"
                  />
                </Field>

                <Field label="Password">
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl bg-zinc-900/60 hairline px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:hairline-strong transition-all duration-300 ease-spring"
                  />
                </Field>

                {error && (
                  <p className="text-xs text-rose-400 animate-fade-up">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="group w-full rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 disabled:opacity-60 px-3 py-2.5 text-sm font-medium text-white shadow-glow-indigo transition-all duration-300 ease-overshoot active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <span>{submitting ? 'Signing in…' : 'Sign in'}</span>
                  <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-300 ease-overshoot group-hover:translate-x-0.5">
                    <span className="text-xs leading-none">→</span>
                  </span>
                </button>
              </form>
            ) : (
              <form onSubmit={onSubmitMfa} className="space-y-4">
                <Field label="Two-factor code">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="6-digit code or backup code"
                    className="w-full rounded-xl bg-zinc-900/60 hairline px-3.5 py-2.5 text-sm tracking-[0.3em] text-center font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:hairline-strong transition-all duration-300 ease-spring"
                  />
                  <p className="mt-2 text-[11px] text-zinc-500">
                    From your authenticator app, or use one of your saved backup codes.
                  </p>
                </Field>

                {error && (
                  <p className="text-xs text-rose-400 animate-fade-up">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !code.trim()}
                  className="group w-full rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 disabled:opacity-60 px-3 py-2.5 text-sm font-medium text-white shadow-glow-indigo transition-all duration-300 ease-overshoot active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <span>{submitting ? 'Verifying…' : 'Verify'}</span>
                  <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-300 ease-overshoot group-hover:translate-x-0.5">
                    <span className="text-xs leading-none">→</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep({ kind: 'credentials' });
                    setCode('');
                    setError(null);
                  }}
                  className="w-full text-xs text-zinc-500 hover:text-zinc-300 transition-colors duration-200"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>

        {step.kind === 'credentials' && (
          <p className="mt-6 text-xs text-center text-zinc-500">
            Admin invite required. Contact PGOC IT to be added.
          </p>
        )}

        <p className="mt-3 text-[10px] text-center font-mono">
          <span
            className={`inline-flex items-center gap-1.5 ${
              serverStatus === 'connecting'
                ? 'text-zinc-600'
                : serverStatus === 'ok'
                  ? 'text-emerald-500'
                  : 'text-rose-500'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                serverStatus === 'connecting'
                  ? 'bg-zinc-600'
                  : serverStatus === 'ok'
                    ? 'bg-emerald-500 shadow-[0_0_8px_rgb(16_185_129_/_0.6)]'
                    : 'bg-rose-500'
              }`}
            />
            {serverStatus === 'connecting' && 'connecting to server…'}
            {serverStatus === 'ok' && 'server online'}
            {serverStatus === 'down' && 'server unreachable'}
          </span>
        </p>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-400 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
